import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Eye, CheckCircle2, Loader2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { Field, Input, Area, Select, Btn, Card, Badge, Sheet, Spinner, download } from "./ui";
import { DocHistory, EmailDocs } from "./SaleExtras";

const KINDS = [["kjopekontrakt", "Kjøpekontrakt"], ["kvittering", "Kvittering"], ["overtakelse", "Overtakelsesprotokoll"]];
const CONDITIONS = [["warranty", "Garantier – Bil / Chassis garanti (tomt = Ingen)"], ["misc", "Diverse"], ["equipment", "Utstyr inkludert i kjøpesummen"],
  ["extras_desc", "Ekstrautstyr solgt i tillegg (valgfritt – kun hvis det betales ekstra)"],
  ["additional_agreements", "Særskilte avtaler"], ["known_defects", "Kjente feil og mangler (kun det du vet – kjøper setter initialer)"]];
const AMOUNTS = [["sale_price", "Sum (kjøpesum)"], ["trade_in", "Innbyttesum"], ["registration_fee", "Omregistrering"],
  ["delivery_cost", "Frakt, levering og registreringskostn."], ["extras_total", "Ekstrautstyr i tillegg (tomt hvis inkludert)"]];
const n = (x) => Number(x) || 0;
const kr = (x) => `${n(x).toLocaleString("nb-NO", { maximumFractionDigits: 2 })} kr`;

const H = ({ children }) => <h3 className="mb-3 font-display text-xl font-bold uppercase">{children}</h3>;

const openPdf = (blob) => {
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank");
  if (!w) { const a = document.createElement("a"); a.href = url; a.download = "dokument.pdf"; a.click(); }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const BuyerCard = ({ d, sec, saleType, setSaleType }) => {
  const [customers, setCustomers] = useState([]);
  useEffect(() => { api.get("/admin/customers").then((r) => setCustomers(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);
  const b = d.buyer;
  const pick = (id) => {
    const c = customers.find((x) => x.id === id);
    sec("buyer", c ? { customer_id: c.id, name: c.name, phone: c.phone, email: c.email } : { customer_id: "" });
  };
  return (
    <Card className="grid gap-3 md:grid-cols-3">
      <div className="md:col-span-3"><H>Kjøper</H></div>
      <Field label="Kjøpstype"><Select value={saleType} onChange={(e) => setSaleType(e.target.value)} options={[["forbruker", "Privatperson (forbrukerkjøp)"], ["naering", "Bedrift (B2B / næringskjøp)"]]} data-testid="sale-type-select" /></Field>
      <Field label="Hent fra kunderegister" className="md:col-span-2"><Select value={b.customer_id} onChange={(e) => pick(e.target.value)} options={[["", "– Velg kunde (valgfritt) –"], ...customers.map((c) => [c.id, `${c.name} ${c.phone}`])]} data-testid="sale-customer-select" /></Field>
      <Field label="Navn / firmanavn"><Input value={b.name} onChange={(e) => sec("buyer", { name: e.target.value })} data-testid="sale-buyer-name" /></Field>
      <Field label={saleType === "naering" ? "Org.nr." : "Fødselsdato"}><Input value={b.id_number} onChange={(e) => sec("buyer", { id_number: e.target.value })} data-testid="sale-buyer-id" /></Field>
      <Field label="Telefon"><Input value={b.phone} onChange={(e) => sec("buyer", { phone: e.target.value })} data-testid="sale-buyer-phone" /></Field>
      <Field label="Adresse"><Input value={b.address} onChange={(e) => sec("buyer", { address: e.target.value })} data-testid="sale-buyer-address" /></Field>
      <Field label="Postnr. og sted"><Input value={b.postal} onChange={(e) => sec("buyer", { postal: e.target.value })} data-testid="sale-buyer-postal" /></Field>
      <Field label="E-post"><Input value={b.email} onChange={(e) => sec("buyer", { email: e.target.value })} data-testid="sale-buyer-email" /></Field>
    </Card>
  );
};

const VehicleCard = ({ d, sec }) => (
  <Card className="grid gap-3 md:grid-cols-3">
    <div className="md:col-span-3"><H>Kjøretøy</H></div>
    {[["reg_nr", "Reg.nr"], ["vin", "VIN"], ["make", "Merke"], ["model", "Modell"], ["year", "Årsmodell"], ["first_registration", "1. gang registrert"],
      ["mileage", "Km-stand ved salg"], ["engine", "Motorstyrke / volum"], ["color", "Farge"], ["fuel", "Drivstoff"]].map(([k, l]) => (
      <Field key={k} label={l}><Input value={d.vehicle[k] ?? ""} onChange={(e) => sec("vehicle", { [k]: e.target.value })} data-testid={`sale-vehicle-${k}`} /></Field>
    ))}
  </Card>
);

const PriceCard = ({ d, sec, vatScheme }) => {
  const p = d.price;
  const total = n(p.sale_price) + n(p.registration_fee) + n(p.delivery_cost) + n(p.extras_total);
  const vat = vatScheme === "avgiftspliktig" ? (n(p.sale_price) + n(p.delivery_cost) + n(p.extras_total)) * 0.2 : 0;
  return (
    <Card className="grid gap-3 md:grid-cols-3">
      <div className="md:col-span-3"><H>Kjøpesum</H></div>
      {AMOUNTS.map(([k, l]) => (
        <Field key={k} label={l}><Input type="number" min="0" value={p[k] ?? ""} onChange={(e) => sec("price", { [k]: e.target.value === "" ? null : Number(e.target.value) })} data-testid={k === "sale_price" ? "sale-price-input" : `sale-${k}-input`} /></Field>
      ))}
      <Field label="Salgsdato"><Input type="date" value={p.sale_date} onChange={(e) => sec("price", { sale_date: e.target.value })} data-testid="sale-date-input" /></Field>
      <Field label="Betalingsvilkår" className="md:col-span-3"><Input value={p.payment_terms} onChange={(e) => sec("price", { payment_terms: e.target.value })} placeholder="F.eks. betales før overtakelse" data-testid="sale-payment-terms" /></Field>
      <Field label="MVA-merknad i dokumentene" className="md:col-span-3"><Input value={p.vat_note} onChange={(e) => sec("price", { vat_note: e.target.value })} data-testid="sale-vat-note" /></Field>
      <div className="grid grid-cols-2 gap-2 border border-white/10 p-3 text-sm md:col-span-3 md:grid-cols-4" data-testid="sale-totals">
        <div>Total<div className="font-bold">{kr(total)}</div></div>
        <div>Innbytte<div className="font-bold">{kr(p.trade_in)}</div></div>
        <div>Antatt MVA<div className="font-bold">{kr(vat)}</div></div>
        <div>Totalsum å betale<div className="font-bold text-[var(--red)]" data-testid="sale-to-pay">{kr(total - n(p.trade_in))}</div></div>
      </div>
      <p className="text-xs text-zinc-400 md:col-span-3" data-testid="sale-vat-summary">{vatScheme === "avgiftspliktig" ? "Avgiftspliktig salg: 25 % MVA inkludert i beløpene." : "MVA-fritatt bruktbil: 0 % MVA."} Endres under «MVA ved salg» på bilen. Kontroller med regnskapsfører ved tvil.</p>
    </Card>
  );
};

const ConditionsCard = ({ d, sec, saleType }) => (
  <Card className="space-y-3">
    <H>Garantier, utstyr og avtaler</H>
    <p className="text-xs text-zinc-400">{saleType === "naering" ? "Næringskjøp: kjøpsloven gjelder. Vilkårene nedenfor skrives i kontrakten." : "Forbrukerkjøp: lovpålagte vilkår etter forbrukerkjøpsloven (reklamasjonsrett 2/5 år) legges automatisk inn i kontrakten."}</p>
    {CONDITIONS.map(([k, l]) => <Field key={k} label={l}><Area value={d.conditions[k] ?? ""} onChange={(e) => sec("conditions", { [k]: e.target.value })} data-testid={`sale-cond-${k}`} /></Field>)}
    {saleType === "naering" && <Field label="Vilkår for næringskjøp (B2B)"><Area rows={4} value={d.conditions.b2b_terms} onChange={(e) => sec("conditions", { b2b_terms: e.target.value })} data-testid="sale-cond-b2b" /></Field>}
  </Card>
);

const PaymentCard = ({ d, sec }) => (
  <Card className="grid gap-3 md:grid-cols-4">
    <div className="md:col-span-4"><H>Mottatt betaling (kvittering)</H></div>
    <Field label="Mottatt beløp"><Input type="number" min="0" value={d.payment.received_amount ?? ""} onChange={(e) => sec("payment", { received_amount: e.target.value === "" ? null : Number(e.target.value) })} data-testid="sale-received-amount" /></Field>
    <Field label="Dato mottatt"><Input type="date" value={d.payment.received_date} onChange={(e) => sec("payment", { received_date: e.target.value })} data-testid="sale-received-date" /></Field>
    <Field label="Betalingsmåte"><Input value={d.payment.method} onChange={(e) => sec("payment", { method: e.target.value })} data-testid="sale-received-method" /></Field>
    <Field label="Referanse"><Input value={d.payment.reference} onChange={(e) => sec("payment", { reference: e.target.value })} data-testid="sale-received-ref" /></Field>
    <label className="flex items-center gap-2 text-sm text-zinc-200 md:col-span-4">
      <input type="checkbox" checked={d.payment.confirmed} onChange={(e) => sec("payment", { confirmed: e.target.checked })} data-testid="sale-payment-confirmed" />
      Jeg bekrefter at beløpet faktisk er mottatt (kreves for kvittering)
    </label>
  </Card>
);

const HandoverCard = ({ d, sec }) => {
  const h = d.handover;
  return (
    <Card className="grid gap-3 md:grid-cols-4">
      <div className="md:col-span-4"><H>Overlevering</H></div>
      <Field label="Km-stand ved levering"><Input type="number" min="0" value={h.delivery_mileage} onChange={(e) => sec("handover", { delivery_mileage: e.target.value })} data-testid="sale-handover-mileage" /></Field>
      <Field label="Antall nøkler"><Input type="number" min="0" value={h.keys_count} onChange={(e) => sec("handover", { keys_count: e.target.value })} data-testid="sale-handover-keys" /></Field>
      <Field label="Levert dato og tid" className="md:col-span-2"><Input type="datetime-local" value={h.delivery_datetime} onChange={(e) => sec("handover", { delivery_datetime: e.target.value })} data-testid="sale-handover-datetime" /></Field>
      <label className="flex items-center gap-2 text-sm text-zinc-200"><input type="checkbox" checked={h.summer_wheels} onChange={(e) => sec("handover", { summer_wheels: e.target.checked })} data-testid="sale-handover-summer" /> Sommerhjul</label>
      <label className="flex items-center gap-2 text-sm text-zinc-200"><input type="checkbox" checked={h.winter_wheels} onChange={(e) => sec("handover", { winter_wheels: e.target.checked })} data-testid="sale-handover-winter" /> Vinterhjul</label>
      <Field label="Merknad hjul/dekk" className="md:col-span-2"><Input value={h.wheels_note} onChange={(e) => sec("handover", { wheels_note: e.target.value })} data-testid="sale-handover-wheels-note" /></Field>
      <Field label="Servicehistorikk og dokumenter overlevert" className="md:col-span-2"><Area value={h.documents_handed} onChange={(e) => sec("handover", { documents_handed: e.target.value })} data-testid="sale-handover-docs" /></Field>
      <Field label="Kjente feil og utestående forhold" className="md:col-span-2"><Area value={h.outstanding_issues} onChange={(e) => sec("handover", { outstanding_issues: e.target.value })} data-testid="sale-handover-issues" /></Field>
      <Field label="Betalingsbekreftelse" className="md:col-span-4"><Input value={h.payment_note} onChange={(e) => sec("handover", { payment_note: e.target.value })} placeholder="F.eks. Hele kjøpesummen er mottatt 12.01.2026" data-testid="sale-handover-payment" /></Field>
    </Card>
  );
};


export const SaleDocs = ({ vehicle, onClose }) => {
  const [s, setS] = useState(null);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const [asking, setAsking] = useState(false);
  const [log, setLog] = useState([]);
  const reloadDocs = async (withDraft = false) => {
    try {
      const [r, l] = await Promise.all([api.get(`/admin/vehicles/${vehicle.id}/sale`), api.get(`/admin/vehicles/${vehicle.id}/email-log`)]);
      setS(r.data); setLog(Array.isArray(l.data) ? l.data : []);
      if (withDraft) setD(r.data.draft);
    } catch (e) { toast.error(errorText(e)); }
  };
  const load = () => reloadDocs(true);
  useEffect(() => { load(); }, [vehicle.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const sec = (k, patch) => setD((x) => ({ ...x, [k]: { ...x[k], ...patch } }));
  const run = async (kind, preview) => {
    setBusy(`${kind}-${preview}`);
    try {
      if (preview) {
        const r = await api.post(`/admin/vehicles/${vehicle.id}/documents/${kind}`, d, { params: { preview: true }, responseType: "blob" });
        openPdf(r.data);
      } else {
        const r = await api.post(`/admin/vehicles/${vehicle.id}/documents/${kind}`, d);
        toast.success(`${r.data.title} ${r.data.number} lagret`);
        await reloadDocs();
        download(`/admin/vehicle-docs/${r.data.id}/download`, r.data.filename);
      }
    } catch (e) {
      const msg = e?.response?.data instanceof Blob ? JSON.parse(await e.response.data.text())?.detail : errorText(e);
      toast.error(msg || "Noe gikk galt");
    } finally { setBusy(""); }
  };
  const saveDraft = async () => {
    setBusy("draft");
    try { await api.put(`/admin/vehicles/${vehicle.id}/sale`, d); toast.success("Utkast lagret"); await reloadDocs(); } catch (e) { toast.error(errorText(e)); } finally { setBusy(""); }
  };
  const confirm = async () => {
    setBusy("confirm");
    try { await api.post(`/admin/vehicles/${vehicle.id}/handover/confirm`); toast.success("Overlevering bekreftet"); setAsking(false); await reloadDocs(); } catch (e) { toast.error(errorText(e)); } finally { setBusy(""); }
  };
  const hasHandover = s?.docs?.some((x) => x.kind === "overtakelse");
  return (
    <Sheet title={`Salgsdokumenter · ${vehicle.reg_nr || vehicle.make}`} onClose={onClose} testid="sale-sheet" solid>
      {!d ? <Spinner /> : (
        <div className="space-y-6">
          {!s.company?.address && <p className="border border-amber-500/50 p-3 text-sm text-amber-300" data-testid="sale-company-warning">Firmaadresse eller bankkonto mangler. Fyll inn under «Innstillinger».</p>}
          <BuyerCard d={d} sec={sec} saleType={d.sale_type} setSaleType={(v) => setD({ ...d, sale_type: v })} />
          <VehicleCard d={d} sec={sec} />
          <PriceCard d={d} sec={sec} vatScheme={s.vat_scheme} />
          <ConditionsCard d={d} sec={sec} saleType={d.sale_type} />
          <PaymentCard d={d} sec={sec} />
          <HandoverCard d={d} sec={sec} />
          <Card className="space-y-3" data-testid="sale-actions">
            <H>Lag dokumenter</H>
            <p className="text-xs text-zinc-400">«Forhåndsvis» viser PDF uten å lagre. «Lag PDF» lagrer dokumentet med nummer i bilens historikk.</p>
            {KINDS.map(([k, l]) => (
              <div key={k} className="flex flex-wrap items-center gap-2">
                <span className="w-48 font-mono text-xs uppercase tracking-widest text-zinc-300">{l}</span>
                <Btn onClick={() => run(k, true)} disabled={!!busy} data-testid={`sale-preview-${k}`}>{busy === `${k}-true` ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Forhåndsvis</Btn>
                <Btn variant="red" onClick={() => run(k, false)} disabled={!!busy || (k === "kvittering" && !d.payment.confirmed)} data-testid={`sale-generate-${k}`}>{busy === `${k}-false` ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Lag PDF</Btn>
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-3 border-t border-white/10 pt-3">
              {s.delivered ? <Badge tone="green">Levert {s.delivered_at ? new Date(s.delivered_at).toLocaleDateString("nb-NO") : ""}</Badge> : asking ? (
                <div className="flex flex-wrap items-center gap-2" data-testid="sale-confirm-box">
                  <span className="text-sm text-zinc-200">Er bilen overlevert til kjøper? Den markeres som solgt og levert.</span>
                  <Btn variant="red" onClick={confirm} disabled={!!busy} data-testid="sale-confirm-yes">Ja, bekreft</Btn>
                  <Btn onClick={() => setAsking(false)} data-testid="sale-confirm-cancel">Avbryt</Btn>
                </div>
              ) : (
                <Btn onClick={() => setAsking(true)} disabled={!!busy || !hasHandover} data-testid="sale-confirm-handover"><CheckCircle2 size={14} /> Bekreft overlevering</Btn>
              )}
              {!s.delivered && !hasHandover && <span className="text-xs text-zinc-400">Lag overtakelsesprotokollen først.</span>}
            </div>
          </Card>
          <DocHistory docs={s.docs ?? []} reload={reloadDocs} />
          <EmailDocs vehicleId={vehicle.id} docs={s.docs ?? []} buyerEmail={s.draft?.buyer?.email ?? ""} log={log} reload={reloadDocs} />
          <div className="sticky bottom-0 flex flex-wrap gap-2 border-t border-white/10 bg-[#08090b] py-4">
            <Btn variant="red" onClick={saveDraft} disabled={!!busy} data-testid="sale-save-draft">Lagre utkast</Btn>
            <Btn onClick={onClose} data-testid="sale-close">Lukk</Btn>
          </div>
        </div>
      )}
    </Sheet>
  );
};
